"use client";

import { useState } from "react";
import {
  CopyButton,
  DataTable,
  PillGroup,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  HINT,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { downloadText } from "@/lib/tool-kit/download";
import { decimals, percentText } from "@/lib/tool-kit/research-design/format";
import { toCsv, toTsv } from "@/lib/tool-kit/research-design/matrix";
import type {
  DelphiResult,
  Judgement,
  RuleKey,
  Verdict,
} from "@/lib/tool-kit/research-ops/delphi";

export interface DelphiResultLabels {
  roundLegend: string;
  roundName: string;
  colItem: string;
  colN: string;
  colMedian: string;
  colIqr: string;
  colMean: string;
  colAgree: string;
  colDisagree: string;
  colResult: string;
  acrossHeading: string;
  roundHeading: string;
  verdicts: Record<Verdict, string>;
  unmetLead: string;
  unmet: Record<RuleKey, string>;
  noRatings: string;
  countsHeading: string;
  countsLine: string;
  copy: string;
  copied: string;
  downloadCsv: string;
  csvFile: string;
  csvRound: string;
  note: string;
}

function resultText(
  judgement: Judgement,
  labels: DelphiResultLabels,
  withReasons: boolean
): string {
  const base = labels.verdicts[judgement.verdict];
  if (
    !withReasons ||
    judgement.verdict !== "none" ||
    judgement.unmet.length === 0
  ) {
    return base;
  }
  const reasons = judgement.unmet.map((key) => labels.unmet[key]).join("; ");
  return `${base} — ${t(labels.unmetLead, { reasons })}`;
}

export function DelphiResults({
  result,
  labels,
}: {
  result: DelphiResult;
  labels: DelphiResultLabels;
}) {
  const fmt = useToolNumber();
  const [picked, setPicked] = useState<number | null>(null);
  const last = result.roundCount - 1;
  const selected = Math.min(picked ?? last, last);
  const num = (value: number) => decimals(fmt, value, 2);
  const pct = (value: number) => percentText(fmt, value / 100, 0);

  const roundRows = result.items.map((item) => {
    const row = item.rounds[selected];
    const judgement = item.judgements[selected];
    if (!row) {
      return [item.name, "—", "—", "—", "—", "—", "—", labels.noRatings];
    }
    return [
      item.name,
      fmt(row.n),
      num(row.median),
      num(row.iqr),
      num(row.mean),
      pct(row.agree),
      pct(row.disagree),
      resultText(judgement, labels, true),
    ];
  });
  const acrossRows = result.items.map((item) => [
    item.name,
    ...item.rounds.map((row) =>
      row ? `${num(row.median)} · ${pct(row.agree)}` : "—"
    ),
    labels.verdicts[item.judgements[last].verdict],
  ]);
  const csv = [
    [
      labels.csvRound,
      labels.colItem,
      labels.colN,
      labels.colMedian,
      labels.colIqr,
      labels.colMean,
      labels.colAgree,
      labels.colDisagree,
      labels.colResult,
    ],
    ...result.items.flatMap((item) =>
      item.rounds.map((row, index) => [
        index + 1,
        item.name,
        row?.n ?? null,
        row?.median ?? null,
        row?.iqr ?? null,
        row?.mean ?? null,
        row?.agree ?? null,
        row?.disagree ?? null,
        labels.verdicts[item.judgements[index].verdict],
      ])
    ),
  ];
  return (
    <div className="space-y-6">
      <p className="text-foreground text-sm">
        <strong>{labels.countsHeading}:</strong>{" "}
        {result.consensusCounts
          .map((count, index) =>
            t(labels.countsLine, {
              round: fmt(index + 1),
              count: fmt(count),
              items: fmt(result.items.length),
            })
          )
          .join(" ")}
      </p>
      {result.roundCount > 1 ? (
        <PillGroup
          label={labels.roundLegend}
          name="dl-round"
          onChange={(value) => setPicked(Number(value))}
          options={result.consensusCounts.map((_, index) => ({
            value: String(index),
            label: t(labels.roundName, { round: fmt(index + 1) }),
          }))}
          value={String(selected)}
        />
      ) : null}
      <div className="space-y-3">
        <h3 className={WIDGET_H3}>
          {t(labels.roundHeading, { round: fmt(selected + 1) })}
        </h3>
        <DataTable
          head={[
            labels.colItem,
            labels.colN,
            labels.colMedian,
            labels.colIqr,
            labels.colMean,
            labels.colAgree,
            labels.colDisagree,
            labels.colResult,
          ]}
          rows={roundRows}
        />
      </div>
      {result.roundCount > 1 ? (
        <div className="space-y-3">
          <h3 className={WIDGET_H3}>{labels.acrossHeading}</h3>
          <DataTable
            head={[
              labels.colItem,
              ...result.consensusCounts.map((_, index) =>
                t(labels.roundName, { round: fmt(index + 1) })
              ),
              labels.colResult,
            ]}
            rows={acrossRows}
          />
        </div>
      ) : null}
      <div className="flex flex-wrap gap-3">
        <CopyButton
          copiedLabel={labels.copied}
          label={labels.copy}
          text={toTsv(csv)}
        />
        <button
          className={BUTTON_SECONDARY}
          onClick={() =>
            downloadText(labels.csvFile, toCsv(csv), "text/csv;charset=utf-8")
          }
          type="button"
        >
          {labels.downloadCsv}
        </button>
      </div>
      <p className={HINT}>{labels.note}</p>
    </div>
  );
}
